// #profile's tabs and the URL: the hash names the tab, and switching tabs updates it.
// #profile → Settings; #profile&events, #profile&public, #profile&testimonial → that tab, when
// the user can see it. Anything else starting with #profile (an unknown or hidden tab) opens
// Settings and is cleaned up to #profile.
// UX/UI testing, as #register&step=N (fullstack useForcedStep.js): #profile&step=N opens tab N
// (1 Settings, 2 Events, 3 Public, 4 Testimonial) for a tester: an admin or editor, or a verified
// vizbi.test+…@gmail.com address. A test account then sees the editor tabs until the dialog
// closes.
// For anyone else, &step is ignored and cleaned off. Not a security boundary: the editor tabs only
// edit the user's own bio and testimonial.

import {ref, watch} from "vue";

export const PROFILE_HASH = /^profile/; // the dialog opens for any hash starting with #profile
// Settings for everyone, Events for anyone with events (attended, registered or paid for others;
// 2026-10-18); Public and Testimonial for editors and admins
// the Settings tab is labelled Preferences (Seán, 2026-10-27); its id and hash are unchanged
const EVERYONE_TABS = [{id: "settings", label: "Preferences"}, {id: "events", label: "Events"}];
const EDITOR_TABS = [{id: "public", label: "Public"}, {id: "testimonial", label: "Testimonial"}];
const TAB_BY_STEP = {1: "settings", 2: "events", 3: "public", 4: "testimonial"};
// as fullstack getIsTester.js
const TEST_EMAIL = /^vizbi\.test\+[a-z0-9.]+(\+clerk_test)?@gmail\.com$/;

export const getIsProfileHash = (hash) => PROFILE_HASH.test(hash.slice(1));

// alphabetical by label (Seán, 2026-10-27)
export function getVisibleTabs(isEditorOrAdmin, hasEvents = true) {
  const everyone = EVERYONE_TABS.filter((tab) => tab.id !== "events" || hasEvents);
  const tabs = isEditorOrAdmin ? [...everyone, ...EDITOR_TABS] : everyone;
  return tabs.sort((a, b) => a.label.localeCompare(b.label));
}

function getTabHash(tabId) {
  return tabId === "settings" ? "#profile" : `#profile&${tabId}`;
}

// "#profile&public" → {tab: "public", step: null}
// "#profile&step=3" → {tab: "", step: "public"}; an unknown step → ""
function getRequest(hash) {
  const parts = hash.slice("#profile".length).split("&").filter(Boolean);
  const tab = parts.find((part) => !part.includes("=")) || "";
  const params = parts.filter((part) => part.includes("=")).join("&");
  const step = new URLSearchParams(params).get("step");
  return {tab, step: step === null ? null : TAB_BY_STEP[step] || ""};
}

const getIsEditorTab = (tabId) => EDITOR_TABS.some((tab) => tab.id === tabId);

export function getIsVerifiedTestEmail(user) {
  const address = user?.primaryEmailAddress;
  if (address?.verification?.status !== "verified") return false;
  return TEST_EMAIL.test(String(address?.emailAddress || "").toLowerCase());
}

// the tab to show, or null to wait (the roles aren't known yet)
// (Events waits for the account, which says whether there are any)
function getTabToShow({tab, step}, {isEditor, hasRoles, isTester, isTesterView, user, hasEvents}) {
  if ((step !== null || getIsEditorTab(tab)) && !hasRoles) return null;
  if (tab === "events" && !user) return null;
  if (step && isTester) return step;
  // a tester's view shows every tab, Events too without events (&step=2 is followed by
  // #profile&events, which must stay)
  const tabs = getVisibleTabs(isEditor || isTesterView, hasEvents || isTesterView);
  const isVisible = tabs.some((item) => item.id === tab);
  return isVisible ? tab : "settings";
}

// options: {route, router, user, isEditorOrAdmin, hasFetchedRoles, hasEvents} (refs where
// reactive)
export function useProfileTabs({route, router, user, isEditorOrAdmin, hasFetchedRoles, hasEvents}) {
  const activeTab = ref("settings");
  const isTesterView = ref(false); // a test account opened with &step: the editor tabs show
  const sources = [() => route.hash, isEditorOrAdmin, hasFetchedRoles, () => user.value, hasEvents];
  watch(sources, ([hash, isEditor, hasRoles, currentUser, hasAny]) => {
    if (!getIsProfileHash(hash)) return (isTesterView.value = false);
    const request = getRequest(hash);
    const isTester = isEditor || getIsVerifiedTestEmail(currentUser);
    const state = {isEditor, hasRoles, isTester, isTesterView: isTesterView.value,
      user: currentUser, hasEvents: hasAny};
    const tab = getTabToShow(request, state);
    if (tab === null) return;
    if (request.step && isTester) isTesterView.value = true;
    activeTab.value = tab;
    if (hash !== getTabHash(tab)) router.replace({path: route.path, hash: getTabHash(tab)});
  }, {immediate: true});
  function openTab(tabId) {
    router.replace({path: route.path, hash: getTabHash(tabId)});
  }
  return {activeTab, openTab, isTesterView};
}
